import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { useGetBrameCampaignQuery } from 'api/brameCampaigns'
import { useUniqueMenuCategories } from 'api/menuCategories'
import { buildTranslate } from 'locales'
import React from 'react'
import {
  DefaultUrls,
  ExclusiveDealUrls,
  GiftCardUrls,
  OrderUrls,
  UrlType,
  UrlValue,
} from 'scenes/Cms/components/UrlPickerGeneric'

const t = buildTranslate(
  'cms.content.app_home_page.content.block_item.description_link'
)
const tUrl = buildTranslate('cms.components.url_picker')

const inappLinks = [
  ...DefaultUrls,
  ...OrderUrls,
  ...ExclusiveDealUrls,
  ...GiftCardUrls,
]

type Props = {
  url: UrlValue
  className?: string
  brameCampaignId?: number | undefined
}

const DescriptionLink = ({ url, className = '', brameCampaignId }: Props) => {
  const [css] = useStyletron()
  const { menuCategories } = useUniqueMenuCategories()

  const { data: brameCampaign } = useGetBrameCampaignQuery(
    brameCampaignId?.toString() || '',
    { skip: !brameCampaignId || url.inapp !== 'thanx://game' }
  )
  const menuCategoryName = menuCategories.find(
    cat => cat.slug === url.queryParams?.split('category=')[1]
  )?.name

  const getUrlHeader = (url: UrlValue): string => {
    if (
      url.type === UrlType.Inapp &&
      url.inapp.startsWith('thanx://ordering-menu')
    )
      return t('ordering_menu')
    if (url.type === UrlType.External) return t('external_url')
    if (url.type === UrlType.Inapp) return t('page_within_app')
    return ''
  }

  const getUrlDescription = (url: UrlValue): string | undefined => {
    if (url.type === UrlType.External) {
      return url[url.type]
    }
    if (
      url.type === UrlType.Inapp &&
      url.inapp.startsWith('thanx://ordering-menu')
    )
      return menuCategoryName || t('no_category_selected')
    if (url.type === UrlType.Inapp) {
      const inAppLink = inappLinks.find(l => l.link === url[url.type])
      return inAppLink?.name ? tUrl(`${inAppLink.name}`) : ''
    }
    return ''
  }

  if (url.type === UrlType.NoLink) {
    return (
      <div className={className}>
        <Text.BodyText4 tag="p" color="grey90" bold className="mb-xs">
          {t('link_description')}
        </Text.BodyText4>
        <Text.BodyText4 tag="p" color="grey70">
          {t('no_link')}
        </Text.BodyText4>
      </div>
    )
  }

  if (url.type === UrlType.Inapp && url.inapp === 'thanx://game') {
    // If no campaign is selected, don't show anything
    if (!brameCampaign) {
      return <div className={className}></div>
    }

    return (
      <div className={className}>
        <Text.BodyText4 tag="p" color="grey90" bold className="mb-xs">
          {t('link_description')}
        </Text.BodyText4>
        <Text.BodyText4 tag="p" color="grey70">
          {brameCampaign.name}
        </Text.BodyText4>
        {brameCampaign.campaign_url && (
          <Text.BodyText4
            tag="p"
            color="grey70"
            className={css({ overflowWrap: 'anywhere' })}
          >
            {brameCampaign.campaign_url}
          </Text.BodyText4>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      <Text.BodyText4 tag="p" color="grey90" bold className="mb-s">
        {t('link_description')}
      </Text.BodyText4>
      <Text.BodyText4 tag="p" color="grey70">
        {getUrlHeader(url)}
      </Text.BodyText4>
      <Text.BodyText4
        tag="p"
        color="grey70"
        className={css({ overflowWrap: 'anywhere' })}
      >
        {getUrlDescription(url)}
      </Text.BodyText4>
    </div>
  )
}

export default DescriptionLink
